import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';

const Cookies = () => {
  return (
    <div data-testid="cookies-page" className="min-h-screen pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
        >
          <ChevronLeft className="h-4 w-4" />
          Tornar a l'inici
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-foreground mb-8">
            Política de Cookies
          </h1>

          <div className="prose prose-lg max-w-none">
            <p className="text-muted-foreground mb-8">
              Última actualització: {new Date().toLocaleDateString('ca-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <section className="mb-8">
              <h2 className="font-heading text-2xl font-medium text-foreground mb-4">1. Què són les Cookies?</h2>
              <p className="text-foreground/80 mb-4">
                Les cookies són petits fitxers de text que els llocs web emmagatzemen al navegador de l'usuari quan aquest visita una pàgina web. Les cookies permeten al lloc web recordar informació sobre la visita de l'usuari, com ara les preferències i la configuració.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl font-medium text-foreground mb-4">2. Tipus de Cookies que Utilitzem</h2>
              
              <div className="mb-6">
                <h3 className="font-heading text-xl font-medium text-foreground mb-3">2.1. Cookies Tècniques (Necessàries)</h3>
                <p className="text-foreground/80 mb-2">
                  Aquestes cookies són necessàries per al correcte funcionament del lloc web i no es poden desactivar.
                </p>
                <ul className="list-disc pl-6 text-foreground/80 space-y-2">
                  <li><strong>Cistella de compra:</strong> Mantenen els productes seleccionats durant la sessió de compra</li>
                  <li><strong>Sessió d'usuari:</strong> Identifiquen l'usuari durant la navegació</li>
                  <li><strong>Seguretat:</strong> Protegeixen contra activitats fraudulentes</li>
                </ul>
              </div>

              <div className="mb-6">
                <h3 className="font-heading text-xl font-medium text-foreground mb-3">2.2. Cookies de Funcionalitat</h3>
                <p className="text-foreground/80 mb-2">
                  Permeten recordar les preferències de l'usuari per millorar l'experiència de navegació.
                </p>
                <ul className="list-disc pl-6 text-foreground/80 space-y-2">
                  <li><strong>Preferències d'idioma:</strong> Recorden l'idioma seleccionat</li>
                  <li><strong>Configuració del lloc:</strong> Guarden preferències de visualització</li>
                </ul>
              </div>

              <div className="mb-6">
                <h3 className="font-heading text-xl font-medium text-foreground mb-3">2.3. Cookies de Tercers</h3>
                <p className="text-foreground/80 mb-2">
                  Utilitzem serveis de tercers que poden establir cookies:
                </p>
                <ul className="list-disc pl-6 text-foreground/80 space-y-2">
                  <li><strong>Stripe:</strong> Per processar pagaments de manera segura</li>
                  <li><strong>Google Maps:</strong> Per mostrar la ubicació de la botiga</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl font-medium text-foreground mb-4">3. Durada de les Cookies</h2>
              <p className="text-foreground/80 mb-4">
                Les cookies poden ser:
              </p>
              <ul className="list-disc pl-6 text-foreground/80 space-y-2">
                <li><strong>De sessió:</strong> S'eliminen quan tanqueu el navegador</li>
                <li><strong>Persistents:</strong> Romanen al dispositiu fins a una data d'expiració o fins que les elimineu manualment</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl font-medium text-foreground mb-4">4. Com Gestionar les Cookies</h2>
              <p className="text-foreground/80 mb-4">
                Pot configurar el seu navegador per acceptar, rebutjar o eliminar cookies. La manera de fer-ho varia segons el navegador:
              </p>
              <ul className="list-disc pl-6 text-foreground/80 space-y-2">
                <li><strong>Google Chrome:</strong> Configuració &gt; Privadesa i seguretat &gt; Cookies</li>
                <li><strong>Mozilla Firefox:</strong> Opcions &gt; Privadesa i seguretat &gt; Cookies i dades del lloc</li>
                <li><strong>Safari:</strong> Preferències &gt; Privadesa &gt; Cookies</li>
                <li><strong>Microsoft Edge:</strong> Configuració &gt; Cookies i permisos del lloc</li>
              </ul>
              <p className="text-foreground/80 mt-4">
                <strong>Nota:</strong> Si desactiva les cookies necessàries, algunes funcions del lloc web podrien no funcionar correctament, especialment la cistella de compra.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl font-medium text-foreground mb-4">5. Cookies Utilitzades</h2>
              <div className="bg-muted rounded-xl p-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 font-medium text-foreground">Cookie</th>
                      <th className="text-left py-2 font-medium text-foreground">Tipus</th>
                      <th className="text-left py-2 font-medium text-foreground">Durada</th>
                      <th className="text-left py-2 font-medium text-foreground">Finalitat</th>
                    </tr>
                  </thead>
                  <tbody className="text-foreground/80">
                    <tr className="border-b border-border/50">
                      <td className="py-3">cart</td>
                      <td className="py-3">Necessària</td>
                      <td className="py-3">Sessió</td>
                      <td className="py-3">Mantenir la cistella de compra</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-3">admin_logged_in</td>
                      <td className="py-3">Necessària</td>
                      <td className="py-3">Sessió</td>
                      <td className="py-3">Gestió de sessió d'administrador</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl font-medium text-foreground mb-4">6. Actualitzacions</h2>
              <p className="text-foreground/80 mb-4">
                Aquesta Política de Cookies pot ser actualitzada periòdicament per reflectir canvis en les pràctiques de cookies o per altres motius operatius, legals o reglamentaris.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl font-medium text-foreground mb-4">7. Més Informació</h2>
              <p className="text-foreground/80 mb-4">
                Si té preguntes sobre aquesta Política de Cookies, pot contactar-nos a:
              </p>
              <ul className="list-disc pl-6 text-foreground/80 space-y-2">
                <li>Telèfon: 932 040 216</li>
                <li>Adreça: Carrer Capità Arenas, 25, Baixos, 08034 Barcelona</li>
              </ul>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Cookies;